import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { caseSchemaStatusKey } from '@/features/schema/hooks/useCaseSchemaStatus'
import { api } from '@/services/api'
import { ApiError } from '@/services/api/client'
import {
  type SchemaClarificationAnswerBody,
  type SchemaSession,
  type SchemaSessionAcceptBody,
  type SchemaSessionStartBody,
  schemaSessionsApi,
} from '@/services/api/schemaSessions'

// --- Query keys ---

export const schemaSessionKeys = {
  current: (caseId: string) => ['schema-session', 'current', caseId] as const,
  documents: (caseId: string) => ['schema-session', 'documents', caseId] as const,
}

/** A run that is still executing — the page polls and streams while true. */
export function isSessionActive(session: SchemaSession | null | undefined): boolean {
  const status = session?.latestRun?.status
  return status === 'queued' || status === 'running'
}

export function isSessionLocked(session: SchemaSession): boolean {
  return isSessionActive(session) || session.latestRun?.status === 'awaiting_input'
}

/** Whether the workbench has anything to show: a draft or proposal, or a run in
 * flight or stopped on the analyst's answers (its live transcript, with the
 * questions, is the content — a run without files always stops first). */
export function hasWorkbenchContent(session: SchemaSession | null | undefined): boolean {
  return (
    !!(session?.workingSchema ?? session?.draft ?? session?.latestRun?.proposal) ||
    (!!session && isSessionLocked(session))
  )
}

/** Whether that content still needs the analyst: a draft to accept, a prepared
 * proposal, a run to wait for. An accepted, idle session does not — the editor is
 * home again and the workbench reopens on request. */
export function needsAnalyst(session: SchemaSession | null | undefined): boolean {
  if (!session || !hasWorkbenchContent(session)) return false
  const acceptedAndIdle =
    !!session.acceptedSchemaId &&
    !session.draft &&
    !session.pendingProposal &&
    !isSessionLocked(session)
  return !acceptedAndIdle
}

// --- Queries ---

/**
 * The case's current schema session, or null when there is none (the API
 * answers 404). The SSE stream is the live signal (`useSchemaSessionEvents`
 * invalidates this query on `run_finished`); a slow poll while the run
 * executes is the fallback for a dropped stream.
 */
export function useCurrentSchemaSession(caseId: string) {
  return useQuery({
    queryKey: schemaSessionKeys.current(caseId),
    queryFn: async (): Promise<SchemaSession | null> => {
      try {
        return await schemaSessionsApi.current(caseId)
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null
        throw err
      }
    },
    enabled: !!caseId,
    refetchInterval: query => (isSessionActive(query.state.data) ? 15_000 : false),
  })
}

/** The case's documents for the reference-file picker; uploaded sources are readable before extraction. */
export function useSchemaSessionDocuments(caseId: string, enabled: boolean) {
  return useQuery({
    queryKey: schemaSessionKeys.documents(caseId),
    queryFn: () => api.cases.listDocuments(caseId, { limit: 200 }),
    enabled: !!caseId && enabled,
  })
}

// --- Mutations ---

export function useStartSchemaSession(caseId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: SchemaSessionStartBody) => schemaSessionsApi.start(caseId, body),
    onSuccess: session => {
      qc.setQueryData(schemaSessionKeys.current(caseId), session)
    },
  })
}

export function useAnswerSchemaClarification(caseId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ sessionId, body }: { sessionId: string; body: SchemaClarificationAnswerBody }) =>
      schemaSessionsApi.answer(caseId, sessionId, body),
    onSuccess: session => {
      qc.setQueryData(schemaSessionKeys.current(caseId), session)
    },
  })
}

export function useAcceptSchemaSession(caseId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ sessionId, body }: { sessionId: string; body?: SchemaSessionAcceptBody }) =>
      schemaSessionsApi.accept(caseId, sessionId, body),
    onSuccess: session => {
      qc.setQueryData(schemaSessionKeys.current(caseId), session)
      void qc.invalidateQueries({ queryKey: caseSchemaStatusKey(caseId) })
    },
  })
}

export function useDiscardSchemaSession(caseId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (sessionId: string) => schemaSessionsApi.discard(caseId, sessionId),
    onSuccess: () => {
      qc.setQueryData(schemaSessionKeys.current(caseId), null)
    },
  })
}

export function useRepairSchemaProposal(caseId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ sessionId, feedback }: { sessionId: string; feedback: string }) =>
      schemaSessionsApi.repair(caseId, sessionId, feedback),
    onSuccess: session => qc.setQueryData(schemaSessionKeys.current(caseId), session),
  })
}
